import {rewindAndPlay} from '@megaphonenpm/mputils/src/DOM.js';
import {isNil} from '@megaphonenpm/mputils/src/utils.js';
import {Task} from '@megaphonenpm/mputils/src/Task.js';

export class PlayVideoTask extends Task {
  /**
   *
   * @param video An HTML <video> element.
   * @param maxRetryAttempts See Task.
   * @param retryDelay See Task.
   */
  constructor (video, maxRetryAttempts = 5, retryDelay = 25) {
    super(maxRetryAttempts, retryDelay);
    this.name = 'PLAY_VIDEO';
    this.video = video;
    this.muted = this.video.muted;
    this.initiator = '';
  }

  start () {
    super.start();
    this.doTask();
  }

  retry () {
    super.retry();
    this.doTask();
  }

  quit () {
    this.done();
  }

  doTask () {
    // Can't play the video if the video doesn't exist
    if (isNil(this.video)) {
      console.warn(`[${this.initiator} > ${this.name}] Missing video reference. Assign a video before performing task.`);
      this.fail();
    } else {
      if (!this.video.paused) {
        // The video is already playing, so the "play video" task should be considered complete
        console.warn(`[${this.initiator} > ${this.name}] Video is already playing. Ending task...`);
        this.done();
      } else {
        // The video exists and is not already playing, so play it now
        console.warn(`[${this.initiator} > ${this.name}] Attempting to play video:`, this.video.src);
        let playPromise = rewindAndPlay(this.video);

        if (playPromise !== undefined) {
          playPromise.then(() => {
            // The play() Promise resolved. Before continuing, check if the task has already
            // completed. The task could have completed already due to a quit() invocation that
            // occurred between the time that doTask() was called and the current Promise resolution.
            if (!this.complete) {
              if (this.video.paused) {
                // The video's play() Promise resolved, but the video still isn't playing, so the
                // play attempt is considered a failure.
                console.error(`[${this.initiator} > ${this.name}] Video\'s play() Promise resolved, but video.paused is true. Failing task...`, this.video.src, this);
                this.fail();
              } else {
                // The video played successfully
                console.warn(`[${this.initiator} > ${this.name}] Video played successfully. Current video mute state: ${this.video.muted ? 'muted' : 'not muted'}. Video src:`, this.video.src);
                this.done();
              }
            }
          }).catch((e) => {
            // The video failed to play. Before continuing, check if the task has already completed
            // The task could have completed already due to a quit() invocation that occurred
            // between the time that doTask() was called and the current Promise resolution.
            if (!this.complete) {
              if (e.name === 'AbortError') {
                console.warn(`[${this.initiator} > ${this.name}] Browser AbortError occurred.`, this.video.src, e);
                // Ensure the video is not left in a (broken) "playing" state so that the next
                // attempt to play it will not mistakenly end prematurely (because the video appears
                // to be playing already).
                this.video.pause();
                // Allow the pause() request a full execution cycle to take effect. This workaround
                // was added in response to observed behavior on iPhone, where video.paused was true
                // in the subsequent retry attempt even after the above call to this.video.pause().
                setTimeout(() => {
                  this.fail();
                }, 0);
              } else {
                // The video failed to play, most likely because it is unmuted and the browser is
                // currently refusing to play videos with sound.
                console.warn(`[${this.initiator} > ${this.name}] Browser refused to play video. Muting video and attempting to play again...`, this.video, e);

                // Mute the video in the hopes that the browser will allow the next play() attempt
                this.setMuted(true);

                this.fail();
              }
            }
          });
        } else {
          console.warn(`[${this.initiator} > ${this.name}] Browser refused to play video (play() returned undefined).`, this.video, e);
          this.fail();
        }
      }
    }
  }

  setMuted (muted) {
    this.video.setAttribute('muted', muted.toString());
    this.video.muted = muted;
    this.muted = muted;
  }
}
